/**
 * Network Partner types — Hub is the source of truth (post-Bigin).
 *
 * Mirrors `vnext-api/vnext/schemas/network_partner.py`, `np_review.py`
 * and `np_note.py`. Field names follow the API wire shape (snake_case)
 * — same convention the assignment shortlist payloads already use.
 */

import { JURISDICTIONS } from '@/constants';

export type NetworkPartnerStatus = 'prospect' | 'active' | 'suspended' | 'archived' | 'banned';

/** Slim shape for shortlist entries and assignment payloads. */
export interface NetworkPartnerSummary {
  id: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string;
  /** Contact-only; dispatch, chat and Backoffice keep using `email`. */
  secondary_email: string | null;
  company: string | null;
  /** ISO 3166-1 alpha-2 (seeded jurisdictions table). */
  location_country: string | null;
  /** State/region detail token, e.g. `us:new-york` (seeded subjurisdictions). */
  location_subjurisdiction: string | null;
  /** Free-text nearest city. */
  location_city: string | null;
  status: NetworkPartnerStatus | string;
  banned_at: string | null;
  languages: string[];
  backoffice_user_id: number | null;
  /** True only for the partner record this environment names as Armadillo:
   * answered from its registry-order conversation, never through Freshdesk
   * or the partner portal. Absent on an API that predates it. */
  registry_partner?: boolean;
}

/** Whether the partner is Armadillo, the registry-document supplier. */
export function isRegistryPartner(
  partner: Pick<NetworkPartnerSummary, 'registry_partner'> | null | undefined
): boolean {
  return partner?.registry_partner === true;
}

/** Roster list row — the summary plus the partner's scope keys. */
export interface NetworkPartnerListItem extends NetworkPartnerSummary {
  scope_keys: string[];
}

/** One scope the partner offers, with its commercial + coverage detail. */
export interface NetworkPartnerScope {
  scope: string;
  min_price: number | string | null;
  max_price: number | string | null;
  currency: string | null;
  average_tat_days: number | null;
  jurisdictions: string[];
  max_capacity: number | null;
}

export interface NetworkPartnerScopePayload {
  scope: string;
  min_price?: number | string | null;
  max_price?: number | string | null;
  currency?: string | null;
  average_tat_days?: number | null;
  jurisdictions?: string[];
  max_capacity?: number | null;
}

export interface NetworkPartnerDetail extends NetworkPartnerListItem {
  phone: string | null;
  mobile: string | null;
  title: string | null;
  bio: string | null;
  scopes: NetworkPartnerScope[];
  /** The reason recorded when the partner was banned; null unless banned. */
  ban_reason: string | null;
  backoffice_linked_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Create/update payload — PATCH sends only the changed keys; `scopes`
 * on PATCH replaces the partner's whole scope list. */
export interface NetworkPartnerPayload {
  first_name?: string;
  last_name?: string;
  name?: string | null;
  email?: string;
  secondary_email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  title?: string | null;
  company?: string | null;
  location_country?: string | null;
  location_subjurisdiction?: string | null;
  location_city?: string | null;
  /** Archiving and banning have their own endpoints. */
  status?: 'prospect' | 'active' | 'suspended';
  languages?: string[];
  bio?: string | null;
  scopes?: NetworkPartnerScopePayload[];
}

export interface NetworkPartnerBanPayload {
  reason: string;
}

export interface PaginatedNetworkPartners {
  items: NetworkPartnerListItem[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  version?: string | null;
}

export interface SubjurisdictionInfo {
  jurisdiction_code: string;
  name: string;
  token: string;
}

export interface BackofficePartnerStatus {
  linked: boolean;
  reachable: boolean;
  backoffice_user_id: number | null;
  is_active: boolean | null;
  created: boolean | null;
}

// --- Reviews / engagements -------------------------------------------------

export interface NpReview {
  id: string;
  network_partner_id: string;
  backoffice_assignment_id: string;
  report_id: string;
  reviewer_user_id: string | null;
  reviewer_email: string | null;
  timeliness: number;
  quality: number;
  /** The pre-2026-09 "communication" score, renamed in place. */
  ease_of_interaction: number;
  gti_experience: number;
  comments: string | null;
  /** Mean of the four dimensions (computed by the API). */
  overall: number;
  created_at: string | null;
}

export interface NpReviewPayload {
  timeliness: number;
  quality: number;
  ease_of_interaction: number;
  gti_experience: number;
  comments?: string | null;
}

export interface NpEngagement {
  assignment_id: string;
  report_id: string;
  status: string;
  dispatched_at: string | null;
  completed_at: string | null;
  outcome: string | null;
  /** NP submission notes from the Backoffice callback. */
  investigator_notes: string | null;
  np_fee: number | string | null;
  np_currency: string | null;
  items_count: number;
  review: NpReview | null;
}

export interface NpEngagements {
  network_partner_id: string;
  /** ONE page of engagements; the review aggregates cover them all. */
  engagements: NpEngagement[];
  total: number;
  page: number;
  page_size: number;
  /** The list's version token (also the `ETag` header). */
  version: string | null;
  review_count: number;
  avg_timeliness: number | null;
  avg_quality: number | null;
  avg_ease_of_interaction: number | null;
  avg_gti_experience: number | null;
  avg_overall: number | null;
  /** Completed engagements (all pages) and the "Experience with GTI"
   * score the rating screen pre-fills from them. */
  completed_count: number;
  gti_experience_default: number | null;
}

// --- Notes stack -----------------------------------------------------------

export type NpNoteKind = 'manual' | 'legacy' | 'ban' | 'review';

/** The rating behind a `review` entry. */
export interface NpNoteReviewRef {
  backoffice_assignment_id: string;
  report_id: string;
  timeliness: number;
  quality: number;
  ease_of_interaction: number;
  gti_experience: number;
  overall: number;
}

export interface NpNote {
  id: string;
  kind: NpNoteKind | string;
  created_at: string | null;
  author_user_id: string | null;
  author_email: string | null;
  /** Review entries without comments carry null — the rating is the content. */
  body: string | null;
  review: NpNoteReviewRef | null;
}

export interface NpNotesResponse {
  network_partner_id: string;
  items: NpNote[];
  total: number;
  page: number;
  page_size: number;
  version: string | null;
}

export interface NpNotePayload {
  body: string;
}

export type NpNoteSort = 'newest' | 'oldest' | 'rating_asc' | 'rating_desc';

/** The star rating the rating sorts order by and the rating bounds
 * filter on; `overall` is the mean of the four review dimensions. */
export type NpNoteDimension =
  | 'overall'
  | 'timeliness'
  | 'quality'
  | 'ease_of_interaction'
  | 'gti_experience';

/** Search / filter / sort / page for the notes stack. Entries without
 * a rating (manual / legacy / ban notes) sort last under the rating
 * sorts and never pass a rating bound. */
export interface NpNotesQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  kinds?: string[];
  sort?: NpNoteSort;
  dimension?: NpNoteDimension;
  minRating?: number;
  maxRating?: number;
}

export interface NetworkPartnerListFilters {
  statuses?: string[];
  scope?: string;
  jurisdiction?: string;
  language?: string;
  search?: string;
  needsAttention?: boolean;
  page?: number;
  pageSize?: number;
}

// --- Vocabularies (mirror the backend StrEnums / vocab modules) ---

export const NP_STATUS_META: Record<string, { label: string; color: string }> = {
  prospect: { label: 'Prospect', color: 'yellow' },
  active: { label: 'Active', color: 'green' },
  suspended: { label: 'Suspended', color: 'orange' },
  archived: { label: 'Archived', color: 'gray' },
  banned: { label: 'Banned', color: 'red' },
};

/** Mirror of vnext/core/np_scopes.py — a PLACEHOLDER list (the ten
 * capabilities carried over from the CRM cutover) until the definitive
 * scope vocabulary is agreed. Replace here and in the backend together. */
export const NP_SCOPES: { value: string; label: string }[] = [
  { value: 'human-source-enquiry', label: 'Human source enquiry' },
  { value: 'desktop-research', label: 'Desktop research' },
  { value: 'corporate-records-retrieval', label: 'Corporate records retrieval' },
  { value: 'litigation-checks', label: 'Litigation checks' },
  { value: 'public-records-search', label: 'Public records search' },
  { value: 'site-visits', label: 'Site visits' },
  { value: 'asset-tracing', label: 'Asset tracing' },
  { value: 'financial-crime-investigation', label: 'Financial crime investigation' },
  { value: 'geopolitical-risk-analysis', label: 'Geopolitical risk analysis' },
  { value: 'security-risk-analysis', label: 'Security risk analysis' },
];

/** Currencies a scope's price range can be quoted in (mirrors the
 * backend ReportConfig Currency enum). */
export const NP_CURRENCIES: string[] = ['GBP', 'EUR', 'USD'];

export const NP_NOTE_SORTS: { value: NpNoteSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'rating_asc', label: 'Lowest rating' },
  { value: 'rating_desc', label: 'Highest rating' },
];

/** Kind filter options for the notes stack; `all` sends no filter. */
export const NP_NOTE_KIND_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Notes' },
  { value: 'review', label: 'Reviews' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'ban', label: 'Banned' },
];

/** The four review dimensions, in display order. */
export const NP_REVIEW_DIMENSIONS: {
  key: 'timeliness' | 'quality' | 'ease_of_interaction' | 'gti_experience';
  label: string;
  short: string;
}[] = [
  { key: 'timeliness', label: 'Timeliness', short: 'T' },
  { key: 'quality', label: 'Quality', short: 'Q' },
  { key: 'ease_of_interaction', label: 'Ease of interaction', short: 'E' },
  { key: 'gti_experience', label: 'Experience with GTI', short: 'G' },
];

/** Rating dimensions the notes stack can sort / filter on. */
export const NP_NOTE_DIMENSIONS: { value: NpNoteDimension; label: string }[] = [
  { value: 'overall', label: 'Overall' },
  ...NP_REVIEW_DIMENSIONS.map((d) => ({ value: d.key, label: d.label })),
];

/** Mirror of vnext/core/languages.py — the constrained language vocabulary. */
export const NP_LANGUAGES: string[] = [
  'afrikaans',
  'albanian',
  'arabic',
  'azerbaijani',
  'belorussian',
  'bengali',
  'bulgarian',
  'burmese',
  'cambodian',
  'cantonese',
  'croatian',
  'czech',
  'danish',
  'dutch',
  'english',
  'farsi',
  'filipino',
  'french',
  'georgian',
  'german',
  'greek',
  'hebrew',
  'hindi',
  'hungarian',
  'indonesian',
  'italian',
  'japanese',
  'kazakh',
  'korean',
  'kurdish',
  'kyrgyz',
  'lao',
  'lithuanian',
  'macedonian',
  'malay',
  'maltese',
  'mandarin',
  'mongolian',
  'montenegrin',
  'nepali',
  'norwegian',
  'pashto',
  'polish',
  'portuguese',
  'punjabi',
  'romanian',
  'russian',
  'serbian',
  'slovak',
  'slovene',
  'somali',
  'spanish',
  'swahili',
  'swedish',
  'tagalog',
  'thai',
  'tigrinya',
  'turkish',
  'ukrainian',
  'urdu',
  'uzbek',
  'vietnamese',
];

export function scopeLabel(slug: string): string {
  return NP_SCOPES.find((s) => s.value === slug)?.label ?? slug;
}

export function countryName(alpha2: string): string {
  return JURISDICTIONS.find((c) => c.alpha2code === alpha2)?.name ?? alpha2;
}

/** `us:new-york` → "New York". */
export function subjurisdictionName(token: string): string {
  const slug = token.includes(':') ? token.slice(token.indexOf(':') + 1) : token;
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * "Nairobi, Kenya" / "United States (New York)" / "Austin, United States
 * (Texas)" from the structured location parts; null when nothing is set.
 */
export function formatNpLocation(
  partner: Pick<
    NetworkPartnerSummary,
    'location_country' | 'location_subjurisdiction' | 'location_city'
  >
): string | null {
  const token = partner.location_subjurisdiction;
  const country =
    partner.location_country ?? (token?.includes(':') ? token.split(':')[0].toUpperCase() : null);
  const countryPart = country
    ? token
      ? `${countryName(country)} (${subjurisdictionName(token)})`
      : countryName(country)
    : null;
  const parts = [partner.location_city?.trim() || null, countryPart].filter((p): p is string =>
    Boolean(p)
  );
  return parts.length > 0 ? parts.join(', ') : null;
}
